import { z } from 'zod/mini';
import { type $ZodError, type $ZodType, type output } from 'zod/v4/core';

import { humanize } from './misc';

// `zod/mini` and classic `zod` share one core config, so this message applies
// to every schema an app builds, with either API.
z.config({
  customError: (issue) => {
    if (issue.code === 'invalid_type') {
      if (issue.path && typeof issue.path === 'string')
        return humanize(`Please provide a value for ${issue.path}`);
      if (issue.path && typeof issue.path[0] === 'string')
        return humanize(`Please provide a value for ${issue.path[0]}`);
      return 'Please fill in this field';
    }
    return undefined; // Let Zod use default error
  },
});

const Empty = z.object({});

/**
 * Any zod schema, built with `zod/mini` or classic `zod`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is a valid use case
type SchemaType = $ZodType<any, any>;
type SchemaTypeWithId = $ZodType & { id: string | number };

type SchemaInfer<T extends SchemaType = SchemaType> = output<T>;

/**
 * The parsed shape of a Zod form schema.
 *
 * Keep the inferred object intact instead of intersecting it with a flat index
 * signature: form defaults can contain booleans, nested objects, arrays, and
 * every other value the schema permits.
 */
type Values<S extends SchemaType> = output<S>;

/** The error a failed parse returns, from either zod API. */
type ZodError = $ZodError;

export {
  Empty,
  type SchemaInfer,
  type SchemaType,
  type SchemaTypeWithId,
  type Values,
  z,
  type ZodError,
};
